import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Input, Button, Card, message } from 'antd';
import { Link } from 'react-router-dom';
import {
    MailOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    SendOutlined,
    ClockCircleOutlined,
    HomeOutlined
} from '@ant-design/icons';
import { useScrollAnimation } from '../../common/hooks';
import { contactService } from '../../common/services/api';
import './ContactPage.css';

const { TextArea } = Input;

const ContactPage = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);

    // Animation hooks
    const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.1 });
    const [mapRef, mapVisible] = useScrollAnimation({ threshold: 0.1 });

    useEffect(() => {
        setTimeout(() => setPageLoaded(true), 100);
    }, []);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await contactService.sendMessage({
                name: values.name,
                email: values.email,
                phone: values.phone || '',
                subject: values.subject,
                message: values.message
            });

            if (response.success) {
                message.success(response.message || 'Votre message a été envoyé avec succès !');
                form.resetFields();
            } else {
                message.error(response.message || 'Erreur lors de l\'envoi du message');
            }
        } catch (error) {
            message.error(error.message || 'Erreur lors de l\'envoi du message');
        } finally {
            setLoading(false);
        }
    };

    const contactInfo = [
        {
            icon: <MailOutlined />,
            title: 'Email',
            content: 'support@tigoon.ci',
            link: 'mailto:support@tigoon.ci'
        },
        {
            icon: <PhoneOutlined />,
            title: 'Téléphone',
            content: '+225 27 22 20 81 23',
            link: 'tel:+2252722208123'
        },
        {
            icon: <EnvironmentOutlined />,
            title: 'Adresse',
            content: 'Bingerville, Abidjan, Côte d\'Ivoire',
            link: null
        },
        {
            icon: <ClockCircleOutlined />,
            title: 'Horaires',
            content: 'Lun - Ven: 9h - 18h',
            link: null
        }
    ];

    return (
        <div className="contact-page">
            {/* Hero Section - Simple Banner */}
            <section className={`contact-hero ${pageLoaded ? 'banner-loaded' : ''}`}>
                <div className="banner-bg-pattern"></div>
                <div className="container">
                    <h1 className={`banner-title ${pageLoaded ? 'animate-in' : ''}`}>
                        Contact
                    </h1>
                    <p className={`banner-description ${pageLoaded ? 'animate-in' : ''}`}>
                        Une question ? N'hésitez pas à nous contacter
                    </p>
                    <nav className={`breadcrumb ${pageLoaded ? 'animate-in' : ''}`}>
                        <Link to="/"><HomeOutlined /> Accueil</Link>
                        <span className="separator">/</span>
                        <span className="current">Contact</span>
                    </nav>
                </div>
            </section>

            {/* Contact Content */}
            <section
                ref={contentRef}
                className={`contact-content ${contentVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <Row gutter={[48, 48]}>
                        {/* Contact Form */}
                        <Col xs={24} md={14}>
                            <Card className={`contact-form-card ${contentVisible ? 'animate-fade-right is-visible' : 'animate-fade-right'}`}>
                                <span className="section-badge">Message</span>
                                <h2>Envoyez-nous un message</h2>
                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={handleSubmit}
                                >
                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="name"
                                                label="Nom complet"
                                                rules={[{ required: true, message: 'Veuillez entrer votre nom' }]}
                                            >
                                                <Input placeholder="Votre nom" size="large" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="email"
                                                label="Email"
                                                rules={[
                                                    { required: true, message: 'Veuillez entrer votre email' },
                                                    { type: 'email', message: 'Veuillez entrer un email valide' }
                                                ]}
                                            >
                                                <Input placeholder="votre@email.com" size="large" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Form.Item
                                        name="phone"
                                        label="Téléphone"
                                    >
                                        <Input placeholder="+225 XX XX XX XX XX" size="large" />
                                    </Form.Item>
                                    <Form.Item
                                        name="subject"
                                        label="Sujet"
                                        rules={[{ required: true, message: 'Veuillez entrer un sujet' }]}
                                    >
                                        <Input placeholder="Sujet de votre message" size="large" />
                                    </Form.Item>
                                    <Form.Item
                                        name="message"
                                        label="Message"
                                        rules={[{ required: true, message: 'Veuillez entrer votre message' }]}
                                    >
                                        <TextArea
                                            rows={5}
                                            placeholder="Votre message..."
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            size="large"
                                            loading={loading}
                                            icon={<SendOutlined />}
                                            className="submit-btn"
                                        >
                                            Envoyer le message
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </Col>

                        {/* Contact Info */}
                        <Col xs={24} md={10}>
                            <div className={`contact-info ${contentVisible ? 'animate-fade-left is-visible' : 'animate-fade-left'}`}>
                                <span className="section-badge">Coordonnées</span>
                                <h2>Informations de contact</h2>
                                <p className="info-subtitle">
                                    N'hésitez pas à nous contacter par l'un des moyens ci-dessous.
                                    Notre équipe est à votre disposition.
                                </p>
                                <div className="info-cards">
                                    {contactInfo.map((info, index) => (
                                        <Card
                                            key={index}
                                            className="info-card"
                                            style={{ transitionDelay: `${index * 0.1}s` }}
                                        >
                                            <div className="info-icon">{info.icon}</div>
                                            <div className="info-content">
                                                <h4>{info.title}</h4>
                                                {info.link ? (
                                                    <a href={info.link}>{info.content}</a>
                                                ) : (
                                                    <span>{info.content}</span>
                                                )}
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* Map Section */}
            <section
                ref={mapRef}
                className={`map-section ${mapVisible ? 'section-visible' : ''}`}
            >
                <div className={`map-container ${mapVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                    <div className="map-placeholder">
                        <div className="map-icon">
                            <EnvironmentOutlined />
                        </div>
                        <span className="map-title">Notre emplacement</span>
                        <span className="map-address">Bingerville, Abidjan, Côte d'Ivoire</span>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
